import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import { clampCommentSidebarWidth } from "../commentSidebarWidth";

const ResizeHandle = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 8px;
  transform: translateX(-50%);
  z-index: 20;
  touch-action: none;
  cursor: col-resize;
  pointer-events: auto;

  &:before {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 3px;
    border-radius: 20px;
    background-color: var(--primary-cta-transparent-15);
    top: 25px;
    bottom: 28px;
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  &:hover,
  &.isHovered,
  &.isResizing {
    &:before {
      opacity: 1;
    }
  }

  &.isResizing {
    z-index: 10001;

    &:after {
      content: "";
      position: fixed;
      width: 500000px;
      height: 500000px;
      background-color: transparent;
      z-index: 10001;
      left: 0;
      top: 0;
      bottom: 0;
      right: 0;
    }
  }
`;

interface CommentSidebarResizeHandleProps {
  width: number;
  onResize: (width: number) => void;
  onResizeEnd: (width: number) => void;
}

export function CommentSidebarResizeHandle({
  width,
  onResize,
  onResizeEnd,
}: CommentSidebarResizeHandleProps): JSX.Element {
  const [isResizing, setIsResizing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const liveWidthRef = useRef(width);

  const handleMove = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!isResizingRef.current) return;
      event.preventDefault();
      const clientX = event.type === "touchmove"
        ? (event as TouchEvent).touches[0]?.clientX ?? startXRef.current
        : (event as MouseEvent).clientX;
      const nextWidth = clampCommentSidebarWidth(startWidthRef.current + (startXRef.current - clientX));
      liveWidthRef.current = nextWidth;
      onResize(nextWidth);
    },
    [onResize],
  );

  const handleUp = useCallback(
    (event?: Event) => {
      event?.preventDefault();
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      setIsResizing(false);
      onResizeEnd(liveWidthRef.current);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    },
    [onResizeEnd],
  );

  const handleDown = useCallback(
    (event: React.MouseEvent | React.TouchEvent) => {
      const clientX = "touches" in event
        ? event.touches[0]?.clientX ?? 0
        : event.clientX;
      event.preventDefault();
      startXRef.current = clientX;
      startWidthRef.current = width;
      liveWidthRef.current = width;
      isResizingRef.current = true;
      setIsResizing(true);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [width],
  );

  useEffect(() => {
    if (!isResizing) return undefined;

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
    document.addEventListener("touchmove", handleMove, { passive: false });
    document.addEventListener("touchend", handleUp);
    document.addEventListener("touchcancel", handleUp);

    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
      document.removeEventListener("touchmove", handleMove);
      document.removeEventListener("touchend", handleUp);
      document.removeEventListener("touchcancel", handleUp);
    };
  }, [handleMove, handleUp, isResizing]);

  return (
    <ResizeHandle
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize comment sidebar"
      onMouseDown={handleDown}
      onTouchStart={handleDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={classNames({ isResizing, isHovered })}
    />
  );
}
